<template>
  <div v-if="themeOptions && themeOptions.length" role="group" qaid="custom-colors-container">
    <customization-group
      v-for="option in paintSchemeOptions"
      :key="option.id"
      :option="option"
      group="color"
      :theme="selectedTheme"
      @theme-summary-clicked="handleThemeClicked(selectedTheme)"
    />
    <div v-if="selectThemeInspirations.length">
      <div class="grid grid-cols-2 gap-2">
        <inspiration-card
          v-for="inspiration in selectThemeInspirations"
          :key="inspiration.pk"
          :inspiration="inspiration"
          @inspiration-clicked="handleSelectInspirationClicked"
        />
      </div>
    </div>
    <template v-if="filteredThemeOptions.length">
      <pdl-heading size="sm" class="my-2" level="3" qaid="color-marketing-copy">
        {{ $t('product.fdp.customizable.colorThemes.message') }}
      </pdl-heading>
      <theme-card
        v-for="theme in filteredThemeOptions"
        :key="theme.id"
        :theme="theme"
        @theme-clicked="handleThemeClicked"
      />
      <color-selector-drawer
        :is-drawer-open="isColorSelectorDrawerOpen"
        :scheme="pendingScheme"
        :filtered-theme-options="filteredThemeOptions"
        :is-loading-colors="isLoadingColors"
        @close-drawer-no-confirmation="restoreConfirmedSelections"
        @confirmed-selections="handleConfirmSelectionClick"
      />
      <customization-reset-selections-dialog
        :is-visible="isResetSelectionsDialogVisible"
        @reset-selections="handleResetSelections"
        @cancel-reset="handleCancelReset"
      />
    </template>
  </div>
</template>

<script>
import {PdlHeading} from '@pedal/pdl-section-header';
import {mapGetters, mapState, mapActions, mapMutations} from 'vuex';
import ThemeCard from '@/components/containers/buy-zone/customization/ThemeCard.vue';
import ColorSelectorDrawer from '@/components/containers/buy-zone/customization/ColorSelectorDrawer.vue';
import {PdlToastType} from '@/constants/pdl-toast-type';
import CustomizationResetSelectionsDialog from '@/components/containers/buy-zone/customization/CustomizationResetSelectionsDialog.vue';
import InspirationCard from '@/components/containers/buy-zone/customization/InspirationCard.vue';
import CustomizationGroup from '@/components/containers/buy-zone/customization/CustomizationGroup.vue';
import {GROUP_IDS} from '@/familyDetailsPage/store/bike-customization';

export default {
  components: {
    PdlHeading,
    ThemeCard,
    ColorSelectorDrawer,
    CustomizationResetSelectionsDialog,
    InspirationCard,
    CustomizationGroup,
  },
  data() {
    return {
      isColorSelectorDrawerOpen: false,
      confirmedSelections: [],
      isLoadingColors: false,
      isResetSelectionsDialogVisible: false,
      pendingScheme: null,
      confirmedThemeName: '',
    };
  },
  computed: {
    ...mapState('bikeCustomization', [
      'themeOptions',
      'buildId',
      'hasChangedSelections',
      'bikeData',
      'paintSchemeOptions',
    ]),
    ...mapGetters('bikeCustomization', ['selections', 'selectedInspirationId']),

    filteredThemeOptions() {
      return this.themeOptions?.filter((theme) => theme.marketingThemeGroup !== 'Select') || [];
    },

    selectTheme() {
      return this.themeOptions?.find((theme) => theme.marketingThemeGroup === 'Select');
    },

    selectThemeInspirations() {
      return this.selectTheme?.inspirations || [];
    },
    hasChangedTheme() {
      return this.bikeData?.themeName !== this.confirmedThemeName;
    },
    selectedTheme() {
      return (
        this.themeOptions?.find((theme) => {
          return theme.inspirations?.some(
            (inspiration) => inspiration.p1PresetThemeChoiceId === this.selectedInspirationId
          );
        }) || {}
      );
    },
  },
  watch: {
    //fetching the default or saved build color options for display in the color summary list
    //need to wait on theme options and bike data (selectedInspirationId) to be available before fetching paint scheme options
    selectedTheme: {
      handler(value) {
        if (value?.inspirations?.length && !this.paintSchemeOptions?.length) {
          try {
            this.fetchPaintSchemeOptions(this.selectedInspirationId, this.selectedTheme?.inspirations);
          } catch (error) {
            console.error('Error fetching paint scheme options', error);
          }
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions('bikeCustomization', ['updateSelection']),
    ...mapMutations('bikeCustomization', ['setIsCustomizationDrawerOpen', 'setHasChangedSelections']),

    async handleThemeClicked(theme) {
      this.pendingScheme = theme;
      this.confirmedSelections = [...this.selections];
      this.confirmedThemeName = this.bikeData.themeName;
      this.fetchColorOptions(theme.id);
    },

    async fetchColorOptions(themeId) {
      this.isLoadingColors = true;
      this.isColorSelectorDrawerOpen = true;
      this.setIsCustomizationDrawerOpen(true);
      const themeInspirations = this.themeOptions.find((theme) => theme.id === themeId)?.inspirations || [];
      if (!themeInspirations?.length) {
        console.error('No inspirations found for the selected theme:', themeId);
        return;
      }
      try {
        await this.fetchPaintSchemeOptions(themeId, themeInspirations);
      } catch (error) {
        console.error('Error fetching theme options:', error);
        this.isColorSelectorDrawerOpen = false;
        this.setIsCustomizationDrawerOpen(false);
        this.$notify({
          type: PdlToastType.ERROR,
          message: this.$t('errorMessage.unableToProcess'),
        });
      } finally {
        this.isLoadingColors = false;
      }
    },

    async fetchPaintSchemeOptions(themeId, inspirations) {
      const selections = [
        {
          groupId: GROUP_IDS.THEME,
          choiceId: themeId,
          topTierPackageId: this.selections.find((selection) => selection.groupId === GROUP_IDS.THEME)
            ?.topTierPackageId,
        },
      ];

      //Use PK of inspiration that matches with theme group (id: 9) in selections;
      // otherwise, if theme group choice doesn't match with an inspiration, use first inspiration
      const PK =
        inspirations.find((inspiration) => {
          return (
            inspiration.p1PresetThemeChoiceId ===
            this.selections?.find((data) => data.groupId === GROUP_IDS.THEME)?.choiceId
          );
        })?.pk || inspirations?.[0]?.pk;

      if (this.pendingScheme !== null && this.pendingScheme.id !== this.selectedInspirationId) {
        //Theme's default paint scheme options needed when user is considering selecting different theme
        await this.updateSelection({selections, module: 'paint-scheme', presetPK: PK, forceGet: true});
      } else {
        //Loading paint scheme options for saved or cached builds should use saved selections
        await this.updateSelection({selections, module: 'paint-scheme', presetPK: PK});
      }
    },

    async restoreConfirmedSelections() {
      try {
        await this.updateSelection({selections: this.confirmedSelections, module: 'paint-scheme'});
      } catch (error) {
        console.error('Error restoring confirmed selections:', error);
      }
      this.closeColorSelectorDrawer();
    },

    handleConfirmSelectionClick() {
      if (this.hasChangedSelections && this.hasChangedTheme) {
        this.isResetSelectionsDialogVisible = true;
      } else {
        this.completeSuccessfulSelection();
      }
    },

    async handleSelectInspirationClicked(inspiration) {
      this.pendingScheme = this.selectTheme;
      this.confirmedThemeName = this.bikeData.themeName;
      this.confirmedSelections = [...this.selections];
      await this.updateSelection({
        selections: this.selections,
        module: 'paint-scheme',
        presetPK: inspiration.pk,
        forceGet: true,
      });
      this.handleConfirmSelectionClick();
    },

    completeSuccessfulSelection() {
      this.$notify({
        type: PdlToastType.SUCCESS,
        message: this.$t('projectOne.customize.added'),
      });
      this.pendingScheme = null;
      this.confirmedThemeName = this.bikeData.themeName;
      if (this.isColorSelectorDrawerOpen) {
        this.closeColorSelectorDrawer();
      }
    },

    closeColorSelectorDrawer() {
      this.isColorSelectorDrawerOpen = false;
      this.setIsCustomizationDrawerOpen(false);
      this.confirmedThemeName = this.bikeData.themeName;
      this.pendingScheme = null;
    },

    handleResetSelections() {
      this.isResetSelectionsDialogVisible = false;
      this.setHasChangedSelections(false);
      this.completeSuccessfulSelection();
    },

    handleCancelReset() {
      this.isResetSelectionsDialogVisible = false;
      this.restoreConfirmedSelections();
    },
  },
};
</script>
